import { useState } from 'react';
import ButtonGroup from 'react-bootstrap/ButtonGroup';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import HelpIcon from '@mui/icons-material/Help';
import SilenceAlertsButton, { SilenceAlertInput } from 'components/alerts/SilenceAlertsButton';
import Button from 'components/common/Button/Button';
import Toolbar from 'components/common/Toolbar/Toolbar';
import SvgIcon from 'components/common/Icon/SvgIcon';
import { AlertInfo, AlertInput } from 'common/graphqlTypes';
import { useMutation } from '@apollo/client';
import { createMuteCondition, isTemplatedAlert } from 'components/alerts/alert';
import SaveAlertDialog from 'components/alerts/SaveAlertDialog';
import ConfirmDialog from 'components/common/Dialog/ConfirmDialog';
import useAlertPageAlerts from 'components/alerts/useAlertPageAlerts';
import pick from 'lodash/pick';
import * as K from 'common/constants';
import { HELP_ROUTE } from 'components/help/constants';
import history from 'service/customHistory';
import { UrlParams } from 'common/types';
import { useSetSearchParams } from 'hooks/useSetSearchParams';
import {
  ALERT_QUERY,
  CREATE_ALERT,
  DELETE_ALERT_MUTATION,
  SILENCE_ALERTS_MUTATION,
  UNSILENCE_ALERTS_MUTATION,
  UPDATE_ALERT,
} from 'components/alerts/queries';
import ELEMENT from 'common/element';
import './AlertDetailsToolbar.scss';

/**
 * The toolbar for alert details.
 *
 * @prop  alert              The Alert object
 */

type AlertDetailsToolbarProps = {
  alert: AlertInfo;
};

function AlertDetailsToolbar({ alert }: AlertDetailsToolbarProps): JSX.Element {
  const refetchQueries = [
    {
      query: ALERT_QUERY,
      variables: {
        id: alert.alertId,
      },
    },
  ];
  const [silenceAlerts] = useMutation(SILENCE_ALERTS_MUTATION, { refetchQueries });
  const [unSilenceAlerts] = useMutation(UNSILENCE_ALERTS_MUTATION, { refetchQueries });
  const [deleteAlert] = useMutation(DELETE_ALERT_MUTATION, { refetchQueries });
  const [createAlert] = useMutation(CREATE_ALERT, { refetchQueries });
  const [updateAlert] = useMutation(UPDATE_ALERT, {
    update(cache, { data }) {
      const cachedAlertData = cache.readQuery({
        query: ALERT_QUERY,
        variables: {
          id: alert.alertId,
        },
      }) as { alert: AlertInfo } | null;

      const updatedAlert = {
        ...cachedAlertData?.alert,
        ...data.updateAlert,
      };

      cache.writeQuery({
        query: ALERT_QUERY,
        variables: {
          id: data.updateAlert.alertId,
        },
        data: {
          alert: updatedAlert,
        },
      });
    },
  });
  const [showEditAlertDialog, setShowEditAlertDialog] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const alertPageAlerts = useAlertPageAlerts();
  const setSearchParams = useSetSearchParams();

  const closeEditAlertDialog = (): void => {
    setShowEditAlertDialog(false);
  };

  const openEditAlertDialog = (): void => {
    setShowEditAlertDialog(true);
  };

  const redirectToAlerts = (): void => {
    history.replace(K.ROUTE.ALERTS);
  };

  const handleSaveAsNewAlert = (alertToSave: AlertInput): void => {
    createAlert({ variables: { alert: alertToSave } })
      .then(closeEditAlertDialog)
      .then(alertPageAlerts.showUpdateSuccess)
      .catch(alertPageAlerts.showError);
  };

  const handleDeleteAlert = (): void => {
    deleteAlert({
      variables: {
        id: alert.alertId,
      },
    })
      .then(alertPageAlerts.showDeleteSuccess)
      .then(redirectToAlerts)
      .catch(alertPageAlerts.showError);
  };

  const handleUnMuteAlerts = (): void => {
    unSilenceAlerts({
      variables: {
        ids: [alert.alertId],
      },
    })
      .then(alertPageAlerts.showUnMuteSuccess)
      .catch(alertPageAlerts.showError);
  };

  const handleEditAlert = (editedAlert: AlertInput): void => {
    const id = (alert as AlertInfo).alertId;

    updateAlert({
      variables: {
        alert: pick(editedAlert, [
          'alertAddresses',
          'description',
          'gracePeriod',
          'renotifyPeriod',
          'resolutionDelay',
          'trigger',
        ]),
        id,
      },
    })
      .then((response) => {
        const shouldRedirect = alert.trigger !== editedAlert.trigger;
        if (shouldRedirect) {
          // Redirect to New Alert Page
          const { alertId } = response?.data?.updateAlert || {};
          setSearchParams({ id: alertId } as UrlParams);
        }
        closeEditAlertDialog();
        alertPageAlerts.showUpdateSuccess();
      })
      .catch(alertPageAlerts.showError);
  };

  const handleMuteAlert = (silenceInputs: SilenceAlertInput): Promise<void> => {
    const { silenceComment, silenceUntil, selectedOption } = silenceInputs;

    return silenceAlerts({
      variables: {
        silenceComment,
        silenceConditions: [{ alertId: alert.alertId, condition: createMuteCondition(alert.templateParameters) }],
        silenceUntil: silenceUntil || selectedOption,
      },
    })
      .then(alertPageAlerts.showMuteSuccess)
      .catch(alertPageAlerts.showError);
  };

  const muteGroup = (
    <ButtonGroup>
      <Button
        disabled={!alert.silenced}
        onClick={handleUnMuteAlerts}
        leftIcon={<VolumeUpIcon fontSize="small" />}
        testId={ELEMENT.alertDetailsUnmuteButton}
      >
        Unmute
      </Button>
      <SilenceAlertsButton
        disabled={alert.silenced}
        submitForm={handleMuteAlert}
        testId={ELEMENT.alertDetailsMuteButton}
      />
    </ButtonGroup>
  );

  const editDeleteGroup = (
    <ButtonGroup>
      <Button
        onClick={openEditAlertDialog}
        leftIcon={<EditIcon fontSize="small" />}
        testId={ELEMENT.alertDetailsEditButton}
      >
        Edit
      </Button>
      <Button
        onClick={() => setShowConfirmDialog(true)}
        leftIcon={<DeleteIcon fontSize="small" />}
        testId={ELEMENT.alertDetailsDeleteButton}
      >
        Delete
      </Button>
    </ButtonGroup>
  );

  const helpButton = (
    <Button
      href={HELP_ROUTE.HELP_ALERTS}
      leftIcon={<HelpIcon fontSize="small" />}
      openInNewTab
      testId={ELEMENT.alertDetailsHelpButton}
    >
      Help
    </Button>
  );

  const templatedAlertWarning = (
    <div className="AlertDetailsToolbar__TemplateAlertWarningContainer">
      <SvgIcon type="warning" />
      <div className="AlertDetailsToolbar__TemplateAlertWarningText">Templated alerts should be edited via json</div>
    </div>
  );

  const headerItems = isTemplatedAlert(alert)
    ? [templatedAlertWarning, muteGroup, helpButton]
    : [muteGroup, editDeleteGroup, helpButton];

  return (
    <div className="AlertDetailsToolbar">
      <Toolbar className="AlertDetailsToolbar__Toolbar" items={headerItems} />
      <SaveAlertDialog
        title="Edit Alert"
        alert={alert}
        show={showEditAlertDialog}
        close={closeEditAlertDialog}
        handleSubmit={handleEditAlert}
        handleSaveAsNewSubmit={handleSaveAsNewAlert}
      />
      <ConfirmDialog
        title="Confirm Delete Alert"
        text="Are you sure you want to delete this alert?"
        show={showConfirmDialog}
        close={() => setShowConfirmDialog(false)}
        onSubmit={handleDeleteAlert}
        cannotBeUndone={true}
      />
    </div>
  );
}

export default AlertDetailsToolbar;
